const translation = {
	/**
	 * UI component translations organized by component hierarchy
	 *
	 * Examples:
	 *
	 * - t('components.forms.FileInput.defaultCta') - maps to app/components/forms/FileInput.tsx
	 * - t('components.WelcomeCard.title') - maps to app/components/WelcomeCard.tsx
	 * - t('components.activityFeed.ActionsCell.sendEmail') - maps to app/components/activityFeed/ActionsCell.svelte
	 * - t('components.Combomark.vendors') - maps to app/components/Combomark.svelte
	 */
	components: {
		forms: {
			AsyncMultiSelectCombobox: {
				typeToSearch: "Type to search…",
				nothingFound: "Nothing found.",
				loading: "Loading…",
			},
			RegionCombobox: {
				nothingFound: "Nothing found.",
			},
			FileInput: {
				defaultCta: "Press or drag here to select a file",
				multipleCta: "{{count}} files",
			},
			TermsAcceptanceField: {
				acceptanceText:
					"I accept the Discover.Market Portal <1>{{destinationTitle}}</1>.",
				errorText: "The {{destinationTitle}} must be accepted.",
			},
			user: {
				AppNotificationsForm: {
					title: "Notification settings",
					channelEmail: "Email",
					disableAllTitle: "Disable all",
					disableAllDescription:
						"Prevent all notifications from being sent. Transactional emails such as account updates are always sent.",
					saveButtonText: "Save",
					processingLabel: "Saving…",
					successMessage: "Notification preferences saved",
					errorMessage: "Failed to save preferences. Please try again.",
					notifications: {
						portalUserProductScanNotification: {
							label: "Product scan",
							description:
								"Sent when a user scans one of your mapped products. Sent at most once every 12 hours per user.",
						},
						portalUserNewMessageNotification: {
							label: "New message",
							description:
								"Sent when you receive a new message while offline. Sent at most once every 4 hours per conversation.",
						},
						portalUserDailyRecapNotification: {
							label: "Daily recap",
							description:
								"A daily summary of buyer engagement with your products, including top accounts and recent saves.",
						},
						portalUserWeeklyRecapNotification: {
							label: "Weekly recap",
							description:
								"A weekly summary of buyer engagement with your products, including top accounts, products, and recent activity.",
						},
					},
				},
				TwoFactorConfirmationForm: {
					introText:
						"To complete enabling two-factor authentication, scan the QR code below using your authenticator app. Alternatively, you can manually configure your app by entering the setup key.",
					instructionText:
						"Once configured, enter the one-time password generated by your authenticator app to verify and enable two-factor authentication.",
					unableToScanPrefix: "Unable to scan? You can use the ",
					setupKeyButtonText: "setup key",
					unableToScanSuffix: " to manually configure your authenticator app.",
					scanQrCodePrefix: "You can also scan the ",
					qrCodeButtonText: "QR code",
					scanQrCodeSuffix: ".",
					oneTimePasswordLabel: "One-time password",
					verifyButtonText: "Verify one-time-password",
				},
				TwoFactorSettingsForm: {
					introText:
						"When two-factor authentication is enabled, you will be prompted to provide a secure one-time password during login. This token can be retrieved from any authenticator app, such as Google Authenticator, 1Password, or Authy.",
					enableButtonText: "Enable",
					enabledText: "Two-factor authentication is enabled.",
					recoveryCodesIntroText:
						"These recovery codes can be used to regain access to your account if you lose access to your two-factor authentication device. Store the recovery codes below in a secure place like a password manager.",
					recoveryCodesWarningText:
						"These recovery codes will not be shown again. If you lose them, you can regenerate a new set of recovery codes below.",
					regenerateButtonText: "Regenerate recovery codes…",
					disableButtonText: "Disable…",
					disableConfirmationText:
						"Are you sure you want to disable two-factor authentication?",
					regenerateConfirmationText:
						"Are you sure you want to regenerate your recovery codes? This action cannot be undone.",
				},
				UserSettingsForm: {
					legalNameLabel: "Legal name",
					legalNameHint: "Used for official forms",
					displayNameLabel: "First name/display name",
					displayNameHint: "Used in emails and other general communications",
					avatarLabel: "Avatar",
					avatarHint:
						"Upload a profile picture. Displayed in conversations with designers.",
					removeAvatarAriaLabel: "Remove avatar",
					forgotPasswordButtonText: "Forgot password?",
					saveButtonText: "Save",
					processingLabel: "Saving…",
					successMessage: "Settings saved successfully",
					errorMessage: "Failed to save settings. Please try again.",
				},
			},
		},
		filterBar: {
			InteractionRangeFilter: {
				placeholder: "All time",
			},
		},
		shared: {
			InteractionPeriodSelect: {
				periods: {
					last7Days: "Last 7 days",
					last14Days: "Last 14 days",
					last30Days: "Last 30 days",
					last90Days: "Last 90 days",
					last6Months: "Last 6 months",
					lastYear: "Last year",
					last2Years: "Last 2 years",
				},
			},
		},
		activityFeed: {
			ActivityFeedTable: {
				emptyMessage: "No data available",
				headers: {
					product: "Product",
					engagementType: "Engagement type",
					account: "Account",
					region: "Region",
					representativeName: "Rep. name",
					actions: "Actions",
				},
			},
			ProductActivityFeed: {
				emptyMessage: "No data available",
				productInteraction: {
					description: {
						scan: "<1>{{productName}}</1> by {{vendorName}} was scanned by {{userName}} at <2>{{accountName}}</2>",
						view: "<1>{{productName}}</1> by {{vendorName}} was viewed by {{userName}} at <2>{{accountName}}</2>",
						addToCollectionInspiration:
							"<1>{{productName}}</1> by {{vendorName}} was added to an inspiration collection by {{userName}} at <2>{{accountName}}</2>",
						addToCollectionClient:
							"<1>{{productName}}</1> by {{vendorName}} was added to a client collection by {{userName}} at <2>{{accountName}}</2>",
						addToCollection:
							"<1>{{productName}}</1> by {{vendorName}} was added to a collection by {{userName}} at <2>{{accountName}}</2>",
					},
				},
			},
			AccountActivityFeed: {
				emptyMessage: "No interactions available",
				accountInteraction: {
					description: {
						scan: "<1>{{productName}}</1> was scanned by {{userName}} at <2>{{accountName}}</2>",
						view: "<1>{{productName}}</1> was viewed by {{userName}} at <2>{{accountName}}</2>",
						addToCollection:
							"<1>{{productName}}</1> was added to a collection by {{userName}} at <2>{{accountName}}</2>",
					},
				},
			},
		},
		settings: {
			PersonalAccessTokensCard: {
				title: "Personal access tokens",
				description:
					"Personal access tokens allow external applications to authenticate with the Portal API. Treat your tokens like passwords and keep them secure.",
				manageButton: "Manage personal access tokens",
			},
			DataExportCard: {
				title: "Data export",
				description:
					"Download your interaction data in a portable format. This includes account information, contact details, and interaction history.",
				exportButton: "Export data",
			},
			modals: {
				CreateTokenModal: {
					title: "Create personal access token",
					nameLabel: "Token name",
					namePlaceholder: "My API token",
					scopesLabel: "Scopes",
					nameRequired: "Token name is required",
					scopesRequired: "At least one scope must be selected",
					cancelButton: "Cancel",
					createButton: "Create token",
					scopes: {
						read: {
							name: "Read access",
							description:
								"Read access to portal data including analytics, products, and territories",
						},
						export: {
							name: "Export access",
							description: "Export portal data to various formats",
						},
					},
				},
				TokenCreatedModal: {
					title: "Personal access token created",
					description: "Token {{name}} has been created successfully.",
					securityNotice: {
						title: "Important security notice",
						description:
							"This token will not be displayed again. Make sure to copy it now and store it in a secure location.",
					},
					tokenLabel: "Your personal access token",
					scopesLabel: "Token scopes",
					copyButton: "Copy",
					copiedButton: "Copied!",
					doneButton: "Done",
				},
			},
		},
		layout: {
			Sidebar: {
				analytics: {
					title: "Analytics",
					dashboard: "Dashboard",
					activityFeed: "Activity feed",
					accounts: "Accounts",
					productPerformance: "Product performance",
					territoryPerformance: "Territory performance",
				},
				messaging: {
					title: "Messaging",
					index: "Your conversations",
					news: "News channel",
					newsNative: "News native",
					repConversations: "Rep conversations",
					conversations: "Conversations",
				},
				tools: {
					title: "Tools",
					qrCodes: "QR code generator",
					barcodeMapping: "Barcode mapping",
				},
				settings: {
					title: "Settings",
					user: "Your settings",
					territories: "Territories",
					portalAccountVendorLinks: "Vendors",
					portalUsers: "Manage users",
					subscription: "Manage subscription…",
					support: "Contact support",
					signOut: "Sign out",
				},
			},
		},
		headers: {
			ImpersonationHeader: {
				impersonating: "Impersonating {{email}}",
				signOut: "Sign out",
			},
		},
		accounts: {
			interactions: {
				InteractionTable: {
					emptyMessage: "No data available",
					filters: {
						sortBy: "Sort by",
						sortDirection: "Sort direction",
						cursor: "Cursor",
						perPage: "Per page",
					},
					headers: {
						timestamp: "Timestamp",
						engagementType: "Engagement type",
						designer: "Designer",
						account: "Account",
						region: "Region",
						actions: "Actions",
						product: "Product",
					},
				},
			},
		},
		messaging: {
			CustomChannelHeader: {
				channelOptions: "Channel options",
				leaveConversation: "Leave conversation…",
				editMembers: "Members…",
				membersOnline: "{{memberCount}} members, {{onlineCount}} online",
				members_one: "{{count}} member",
				members_other: "{{count}} members",
				online_one: "{{count}} online",
				online_other: "{{count}} online",
			},
			CustomSystemMessage: {
				productInquiry: "Product inquiry",
				vendorInquiry: "Vendor inquiry",
			},
			EmptyStateIndicator: {
				message:
					"You don't have any conversations yet. Messages from designers will appear here!",
			},
			MemberItem: {
				youSuffix: " (You)",
				addingText: "Adding…",
				addText: "Add",
				repType: "Rep",
				designerType: "Designer",
			},
			MembersModal: {
				title: "Channel Members",
				loadingText: "Loading members…",
				designersHeader_one: "Designer",
				designersHeader_other: "Designers ({{count}})",
				viewDesigner: "View account…",
				currentMembersHeader: "Current members ({{count}})",
				noMembersFound: "No members found",
				addRepsHeader: "Add reps",
				searchPlaceholder: "Search reps…",
				noRepsFoundSearch: "No reps found matching your search",
				noRepsAvailable: "No reps available to invite",
				closeButton: "Close",
			},
		},
		barcode: {
			AddBarcodeMappingModal: {
				title: "Manage barcode mappings",
				description: "Add or remove barcode mappings for this product",
				existingMappingsHeader: "Existing barcode mappings",
				deleteConfirm: "Are you sure you want to delete this barcode mapping?",
				deleteButtonTitle: "Delete barcode mapping",
				addNewHeader: "Add new barcode",
				cancelScanning: "Cancel scanning",
				inputPlaceholder: "Enter barcode or scan with camera",
				addingButton: "Adding…",
				addButton: "Add barcode",
				pleaseEnterBarcode: "Please enter a barcode",
				failedToAdd: "Failed to add barcode mapping",
			},
		},
		products: {
			form: {
				ProductForm: {
					basicDetails: "Basic Details",
					name: "Name",
					sku: "SKU",
					description: "Description",
					images: "Images",
					category: "Category",
					categoryLabel: "Product Category",
					categoryPlaceholder: "Select a category...",
					attributes: "Attributes",
					attributesDescription:
						"Tag your product with attributes to help buyers find it.",
					colors: "Colors",
					colorsPlaceholder: "Select colors...",
					colorsHint: "Type to search available colors",
					materials: "Materials",
					materialsPlaceholder: "Select materials...",
					materialsHint: "Type to search available materials",
					patterns: "Patterns",
					patternsPlaceholder: "Select patterns...",
					patternsHint: "For textiles: stripes, florals, geometric, etc.",
					endUses: "End Use",
					endUsesPlaceholder: "Select end uses...",
					endUsesHint: "For textiles: upholstery, drapery, bedding, etc.",
					pricing: "Pricing",
					pricingDescription: "Set your product's pricing tiers in USD.",
					wholesalePrice: "Wholesale Price",
					tradePrice: "Trade Price",
					imapPrice: "IMAP Price",
					priceHint: "Enter price in dollars",
					inventory: "Inventory",
					inventoryDescription: "Track stock levels and product availability.",
					quantityType: "Quantity Type",
					inventoryRemaining: "Inventory Remaining",
					availability: "Availability",
					available: "Available",
					discontinued: "Discontinued",
					notes: "Notes",
					internalNotes: "Internal Notes",
					internalNotesHint: "These notes are only visible to your team",
					saveSuccess: "Product saved",
					saveError: "Failed to save product. Please try again.",
				},
			},
			ProductImageUploader: {
				uploading: "Uploading image...",
				uploadingMultiple: "Uploading {{current}} of {{total}}...",
				uploadSuccess: "Image uploaded successfully",
				uploadSuccessMultiple_one: "Image uploaded successfully",
				uploadSuccessMultiple_other: "{{count}} images uploaded successfully",
				uploadError: "Failed to upload image. Please try again.",
				uploadErrorMultiple_one: "Failed to upload image. Please try again.",
				uploadErrorMultiple_other:
					"Failed to upload {{count}} images. Please try again.",
				uploadPartialSuccess:
					"Uploaded {{successCount}} of {{totalCount}} images. {{failedCount}} failed.",
				deleteSuccess: "Image deleted successfully",
				deleteError: "Failed to delete image. Please try again.",
				featuredSuccess: "Featured image updated",
				featuredError: "Failed to update featured image. Please try again.",
				featured: "Featured",
				setFeatured: "Set as featured",
				delete: "Delete image",
				dropzone: "Drop images here or click to browse",
				formats: "JPEG or PNG format",
				emptyHint: "Add your first product image to get started",
			},
			interactions: {
				InteractionTable: {
					emptyMessage: "No data available",
					filters: {
						sortBy: "Sort by",
						sortDirection: "Sort direction",
						cursor: "Cursor",
						perPage: "Per page",
					},
					headers: {
						timestamp: "Timestamp",
						engagementType: "Engagement type",
						designer: "Designer",
						account: "Account",
						region: "Region",
						actions: "Actions",
					},
				},
			},
		},
		territories: {
			form: {
				DatasetPicker: {
					title_USStateBoundaries: "US states",
					title_NpaBoundaries: "US area codes",
					title_USCountyBoundaries: "US counties",
					title_USPostalCodeBoundaries: "US postal codes",

					includedExcluded: "{{included}} included, {{excluded}} excluded",
					included: "{{included}} included",
					excluded: "{{excluded}} excluded",
				},
				TerritoryForm: {
					name: "Name",
					color: "Color",
					clearSelections: "Clear selections…",
					confirmClearSelections:
						"Are you sure you want to remove all selections?",
					fetchingFeatures: "Loading features…",
					delete: "Delete…",
					confirmDelete:
						"Are you sure you want to delete this territory? Any viewers associated with it will be given access to view all vendor data. This can't be undone!",
					areaSelection: {
						title: "Area selection",
						description:
							"Click an area to include it. Click again to exclude it, and a third time to clear it from the selection entirely.",
					},
				},
				TerritoryEditor: {
					advancedOptions: "Advanced options",
					collapseAdvanced: "Hide advanced options",
					advancedOptionsDescription:
						"Paste ZIP codes to fetch and update multiple territories at once.",
					bulkZip: {
						title: "Bulk ZIP code management",
						placeholder: "95370\n94103\n78701",
						description: "Enter newline-separated 5 digit ZIP codes.",
						enteredCount_one: "{{count}} ZIP code pasted",
						enteredCount_other: "{{count}} ZIP codes pasted",
						fetchButton: "Fetch territories",
						fetching: "Fetching…",
						summary: "Entered {{entered}} ZIP codes; {{found}} found.",
						summaryNotFound_one: "{{count}} not found",
						summaryNotFound_other: "{{count}} not found",
						includeAll: "Include all found",
						excludeAll: "Exclude all found",
						clearAll: "Clear from selections",
						showDetails: "Show ZIP details",
						hideDetails: "Hide ZIP details",
						detailsFound_one: "Found ({{count}})",
						detailsFound_other: "Found ({{count}})",
						detailsNotFound_one: "Not found ({{count}})",
						detailsNotFound_other: "Not found ({{count}})",
						status: {
							included: "Included",
							excluded: "Excluded",
							unselected: "Not selected",
						},
					},
				},
			},
			table: {
				filterBar: {
					KeywordFilter: {
						label: "Keyword",
						placeholder: "Search by name…",
					},
				},
				TerritoriesTable: {
					emptyMessage: "No territories found",
				},
				TerritoriesTableColumns: {
					name: "Name",
					createdAt: "Created at",
					actions: "Actions",
				},
			},
		},
		vendors: {
			table: {
				filterBar: {
					KeywordFilter: {
						label: "Keyword",
						placeholder: "Search by vendor name…",
					},
				},
				VendorsTable: {
					emptyMessage: "No vendors found",
				},
				VendorsTableColumns: {
					name: "Name",
					actions: "Actions",
				},
				ActionCell: {
					editComingSoon: "TODO",
				},
			},
		},
		vendorProducts: {
			filterBar: {
				VendorProductsFilterBar: {
					keywordLabel: "Keyword",
					keywordPlaceholder: "Search by name or SKU…",
					categoryLabel: "Category",
					categoryPlaceholder: "All categories",
					statusAll: "All",
					statusPublished: "Published",
					statusUnpublished: "Unpublished",
					statusArchived: "Archived",
				},
			},
			table: {
				VendorProductsTable: {
					emptyMessage: "No products found",
				},
				VendorProductsTableColumns: {
					name: "Name",
					sku: "SKU",
					status: "Status",
					updatedAt: "Updated at",
					published: "Published",
					unpublished: "Unpublished",
					archived: "Archived",
					actions: "Actions",
				},
			},
		},
		Combomark: {
			vendors: "Vendors",
		},
		qrWizard: {
			StepSelectSkus: {
				title: "Select SKUs",
				description:
					'Select a vendor. Then, upload a CSV containing a "SKU" column. Each SKU should match a product in the Discover.Market system for that vendor.',
				demoCsv: "Demo CSV",
				fieldLabel: "SKU CSV file",
				notFoundSkus_one:
					"Warning: 1 SKU couldn't be found (of {{totalCount}}) - {{skus}}. Please contact our support team if you need help.",
				notFoundSkus_other:
					"Warning: {{count}} SKUs couldn't be found (of {{totalCount}}) - {{skus}}. Please contact our support team if you need help.",
				vendorLabel: "Vendor",
				vendorSearchPlaceholder: "Search vendors…",
				vendorEmptyPlaceholder: "Select a vendor",
			},
			StepCustomizeAndExport: {
				title: "Customize & export",
				description:
					"Add an optional analytics tag and adjust the style of your QR codes before downloading.",
				exportTypeLabel: "Export type",
				tagLabel: "Analytics tag",
				exportingLabel_one: "Exporting…",
				exportingLabel_other: "Exporting {{current}} of {{count}}…",
				tagHint:
					"Optional, max 12 characters. Example use: Identify QR codes in a certain location, or for a specific event.",
				LetterPortraitPdf: {
					title: "Letter (Portrait)",
					subtitle_one:
						'{{count}} page, 8.5"×11" PDF\nCan be printed 1, 2, or 4 per sheet',
					subtitle_other:
						'{{count}} pages, 8.5"×11" PDF\nCan be printed 1, 2, or 4 per sheet',
				},
				BusinessCardPortraitPdf: {
					title: "Business card (Portrait)",
					subtitle_one: '{{count}} page, 2"×3.5" PDF',
					subtitle_other: '{{count}} pages, 2"×3.5" PDF',
				},
				BusinessCardLandscapePdf: {
					title: "Business card (Landscape)",
					subtitle_one: '{{count}} page, 3.5"×2" PDF',
					subtitle_other: '{{count}} pages, 3.5"×2" PDF',
				},
				SvgZip: {
					title: "QR images only",
					subtitle: "ZIP of SVG image files",
				},
			},
		},
		barcodeMappings: {
			wizard: {
				StepUploadMappings: {
					title: "Upload mappings",
					demoCsv: "Demo CSV",
					description:
						'Select a vendor. Then, upload a CSV containing "Barcode" and "SKU" columns. Each SKU should match a product in the Discover.Market system. The barcode can be any value up to 2048 characters. Barcodes in the CSV should be the exact value encoded in your existing tags. For example, they could be all numbers, numbers and letters, or a full URL.',
					fieldLabel: "Barcode/SKU CSV",
					vendorLabel: "Vendor",
					vendorSearchPlaceholder: "Search vendors…",
					vendorEmptyPlaceholder: "Select a vendor",
				},
				StepSaveMappings: {
					title: "Save mappings",
					description:
						"Select how you want mappings to be applied, and verify your changes before saving.",
					editTypeLabel: "Edit type",
					saveMappingsButton: "Save mappings",
					newMappings: {
						title: "New mappings",
						description_zero: "No new mappings found.",
						description_one: "One new mapping will be created:",
						description_other: "{{count}} new mappings will be created:",
					},
					existingMappings: {
						title: "Existing mappings",
						description_zero: "No existing mappings found.",
						description_one: "One existing mapping will be removed:",
						description_other: "{{count}} existing mappings will be removed:",
					},
					missingSkuMappings: {
						title: "Missing SKUs",
						description_one:
							"One row contains a SKU that doesn't exist in Discover.Market. It will be ignored.",
						description_other:
							"{{count}} rows contain SKUS that don't exist in Discover.Market. They will be ignored.",
					},
				},
				EditTypeRadio: {
					StoreMappings: {
						title: "Create mappings",
						subtitle: "SKU/Barcode pairs that don't exist yet will be added.",
					},
					DestroyMappings: {
						title: "Destroy mappings",
						subtitle: "SKU/Barcode pairs that already exist will be removed.",
					},
				},
				InputMappingTable: {
					headers: {
						barcode: "Barcode",
						sku: "SKU",
					},
				},
			},
			table: {
				filterBar: {
					KeywordFilter: {
						label: "Keyword",
						placeholder: "Search barcode, SKU, vendor, or product name",
					},
					VendorFilter: {
						label: "Vendor",
						searchPlaceholder: "Search vendors…",
						emptyPlaceholder: "Showing all vendors",
					},
				},
				BarcodeMappingsTable: {
					emptyMessage: "No data available",
					headers: {
						vendorName: "Vendor name",
						productName: "Product name",
						barcode: "Barcode",
						sku: "SKU",
					},
					filters: {
						keyword: "Keyword",
						vendorId: "Vendor",
						sortBy: "Sort by",
						sortDirection: "Sort direction",
						page: "Page",
						perPage: "Per page",
					},
				},
			},
		},
		dashboard: {
			cards: {
				welcomeCard: {
					WelcomeCard: {
						greeting: "Welcome back,",
						name: "{{name}}!",
						subtitle:
							"This is your analytics dashboard. Each section allows you to explore data for your products, your buyers, and more.",
						collectedProductsCount_one:
							"Designers have added {{count}} of your products to a collection.",
						collectedProductsCount_other:
							"Designers have added {{count}} of your products to collections.",
						ctaLabel: "Explore analytics",
					},
				},
				regionMapCard: {
					RegionMapCard: {
						title: "Regional engagement",
						topRegions: "Top regions",
						selectLabel: "Select regional engagement period",
					},
				},
				catalogHealth: {
					CatalogHealthCard: {
						description: "Purchasable products vs. Catalog",
					},
				},
				productEngagementCard: {
					ProductEngagementCard: {
						title: "Product engagement",
						selectLabel: "Select product engagement period",
					},
				},
				productPerformanceTableCard: {
					ProductPerformanceTableCard: {
						title: "Top performing products",
						viewAll: "View all",
						selectLabel: "Select top products period",
						headers: {
							product: "Product",
							totalInteractions: "Total interactions",
							engagement: "Engagement",
							actions: "Actions",
						},
					},
				},
				accountPerformanceTable: {
					AccountPerformanceTableCard: {
						title: "Top performing accounts",
						viewAll: "View all",
						selectLabel: "Select top accounts period",
						headers: {
							account: "Account",
							totalInteractions: "Total interactions",
							views: "Views",
							scans: "Scans",
							collectionAdds: "Collection adds",
							region: "Region",
							actions: "Actions",
						},
					},
				},
				territoryPerformanceTableCard: {
					TerritoryPerformanceTableCard: {
						title: "Top performing territories",
						viewAll: "View all",
						selectLabel: "Select top territories period",
						headers: {
							territory: "Territory",
							views: "Views",
							scans: "Scans",
							collections: "Collections",
							totalInteractions: "Total interactions",
							actions: "Actions",
						},
					},
					ActionCell: {
						viewDetails: "View details",
					},
				},
				territoryPerformanceMapCard: {
					TerritoryPerformanceMapCard: {
						title: "Territory performance map",
						selectLabel: "Select territory performance period",
						topTerritoriesLabel: "Top performing territories",
						headers: {
							territory: "Territory",
							views: "Views",
							scans: "Scans",
							collectionAdds: "Collection adds",
							total: "Total",
							action: "Action",
						},
					},
					TerritoryPerformanceLegend: {
						interactions: "Interactions",
						interactionCount_one: "{{formattedCount}} interaction",
						interactionCount_other: "{{formattedCount}} interactions",
						fewer: "Fewer",
						more: "More",
					},
				},
				performanceMapCard: {
					PerformanceMapCard: {
						regionTitle: "Region performance map",
						territoryTitle: "Territory performance map",
						regionTooltip: "Product interactions aggregated by US state",
						territoryTooltip:
							"Product interactions aggregated by your account's custom territories",
						toggleRegion: "Region performance",
						toggleTerritory: "Territory performance",
						regionSelectLabel: "Select region performance period",
						territorySelectLabel: "Select territory performance period",
						topRegionsLabel: "Top performing regions",
						topTerritoriesLabel: "Top performing territories",
						headers: {
							region: "Region",
							territory: "Territory",
							views: "Views",
							scans: "Scans",
							collectionAdds: "Collection adds",
							total: "Total",
							action: "Action",
						},
					},
					RegionPerformanceLegend: {
						interactions: "Interactions",
						interactionCount_one: "{{formattedCount}} interaction",
						interactionCount_other: "{{formattedCount}} interactions",
						fewer: "Fewer",
						more: "More",
					},
				},
				activityFeed: {
					ActivityFeedCard: {
						title: "Activity feed",
						viewAll: "View all",
					},
				},
				potentialEarnings: {
					PotentialEarningsCard: {
						title: "Potential earnings",
						description: "Client collections, last 90 days",
						comparisonLabel: "vs.",
						selectLabel: "Select potential earnings period",
					},
				},
			},
		},
		Pagination: {
			page_one: "Page",
			page_other: "Pages",
			firstPage: "First page",
			previousPage: "Previous page",
			nextPage: "Next page",
			lastPage: "Last page",
			noResults: "No results",
			resultsCount_one: "Showing {{from}} to {{to}} of {{count}} result",
			resultsCount_other: "Showing {{from}} to {{to}} of {{count}} results",
			perPage: "Per page",
			goToFirstPage: "Go to first page",
			goToPreviousPage: "Go to previous page",
			goToNextPage: "Go to next page",
			goToLastPage: "Go to last page",
			pageInfo: "Page {{currentPage}} of {{totalPages}} {{pageText}}",
			total_one: "1 result",
			total_other: "{{count}} results",
		},
		CategoryPathComboBox: {
			label: "Category",
			selectPrompt: "Filter by category",
			keywordPlaceholder: "Search for a category…",
		},
		SkuFilter: {
			label: "SKU",
			placeholder: "Filter by SKU",
		},
		SkuQuickJump: {
			placeholder: "Jump to product by SKU…",
			notFound: "Product not found",
		},
		ProductSelectionModal: {
			title: "Select a product",
			description:
				'Multiple products found with SKU "{{sku}}". Please select one:',
		},
		ProductPerformanceTable: {
			emptyMessage: "No data available",
			export: "Export",
			exportCsv: "Download as CSV",
			exportXlsx: "Download as Excel",
			filters: {
				territory: "Territory",
				categoryPath: "Category",
				sku: "SKU",
				interactionRange: "Interaction range",
				interactionRangeStart: "Interaction range start",
				interactionRangeEnd: "Interaction range end",
				sortBy: "Sort by",
				sortDirection: "Sort direction",
				cursor: "Cursor",
				perPage: "Per page",
			},
			headers: {
				product: "Product",
				views: "Views",
				scans: "Scans",
				collectionAdds: "Collection adds",
				totalInteractions: "Total interactions",
				engagement: "Engagement",
				actions: "Actions",
			},
		},
		TerritoryPerformanceTable: {
			emptyMessage: "No territory performance data available.",
			export: "Export",
			exportCsv: "Download as CSV",
			exportXlsx: "Download as Excel",
			filters: {
				interactionRange: "Interaction range",
				interactionRangeStart: "Interactions on or after",
				interactionRangeEnd: "Interactions on or before",
				sortBy: "Sort by",
				sortDirection: "Sort direction",
			},
			headers: {
				territory: "Territory",
				views: "Views",
				scans: "Scans",
				collections: "Collections",
				totalInteractions: "Total Interactions",
				actions: "Actions",
			},
		},
		AccountNameFilter: {
			label: "Account name",
			placeholder: "Filter by account name",
		},
		AccountsTable: {
			emptyMessage: "No data available",
			export: "Export",
			exportCsv: "Download as CSV",
			exportXlsx: "Download as Excel",
			exportTooltip:
				"Export all filtered accounts with interaction metrics and primary contact information.",
			filters: {
				accountName: "Account name",
				region: "Region",
				regionAll: "All regions",
				territory: "Territory",
				territoryAll: "All territories",
				territoryUnnamed: "Untitled territory",
				interactionRange: "Interaction range",
				interactionRangeStart: "Interaction range start",
				interactionRangeEnd: "Interaction range end",
				sortBy: "Sort by",
				sortDirection: "Sort direction",
				cursor: "Cursor",
				perPage: "Per page",
			},
			headers: {
				name: "Name",
				interactions: "Interactions",
				views: "Views",
				scans: "Scans",
				collectionAdds: "Collection adds",
				region: "Region",
				firstInteracted: "First interacted",
				lastInteracted: "Last interacted",
				actions: "Actions",
			},
			actionMenu: {
				ariaLabel: "Account actions",
				message: "Message",
			},
		},
		ProductCell: {},
		SubmitButton: {
			processingLabel: "Processing…",
		},
		Notification: {
			imageAlt: "Notification image for {{id}}",
		},
		vendorSelector: {
			VendorSelector: {
				selectAll: "Select all",
				deselectAll: "Deselect all",
				errors: {
					updateFailed: "Update failed",
				},
				triggers: {
					showingAll: "All vendors",
					showingOne: "{{vendorName}}",
					showingSome: "{{count}} vendors",
				},
				titles: {
					loading: "Loading…",
					showingAll: "Showing all vendors",
					showingOne: "Showing 1 vendor",
					showingSome: "Showing {{count}} vendors",
				},
			},
		},
		repMessaging: {
			filterBar: {
				KeywordFilter: {
					label: "Keyword",
					placeholder: "Search representatives…",
				},
			},
			table: {
				RepMessagingTable: {
					emptyMessage: "No representatives found",
				},
				RepMessagingTableColumns: {
					user: "Representative",
					averageResponseTime: "Avg. Response Time",
					unreadCount: "Unread",
					lastActive: "Last Active",
					status: "Status",
					online: "Online",
					offline: "Offline",
					actions: "Actions",
					view: "View",
					averageResponseTimeLt1Minute: "< 1 minute",
					averageResponseTimeLt5Minutes: "< 5 minutes",
					averageResponseTimeLt15Minutes: "< 15 minutes",
					averageResponseTimeLt30Minutes: "< 30 minutes",
					averageResponseTimeLt60Minutes: "< 1 hour",
					averageResponseTimeLt240Minutes: "< 4 hours",
					averageResponseTimeGt240Minutes: "> 4 hours",
				},
			},
			channelList: {
				noMessages: "No messages",
				justNow: "Just now",
				minutesAgo_one: "{{count}} minute ago",
				minutesAgo_other: "{{count}} minutes ago",
				hoursAgo: "{{count}} hour ago",
				hoursAgo_other: "{{count}} hours ago",
				daysAgo: "{{count}} day ago",
				daysAgo_other: "{{count}} days ago",
				vendorChannel: "Vendor {{vendorId}}",
				unknownVendor: "Unknown Vendor",
				members_one: "{{count}} member",
				members_other: "{{count}} members",
				messages_one: "Message",
				messages_other: "Messages",
				lastMessage: "Last message",
			},
			channelPreviewModal: {
				title: "Channel Preview",
				channel_info: "Channel Information",
				channel_preview_title: "Channel preview (max last 30 messages)",
				vendor_label: "Vendor",
				user_avatar_alt: "User",
				unknown_user: "Unknown User",
				attachments_count_one: "{{count}} attachment",
				attachments_count_other: "{{count}} attachments",
				channel_id: "Channel ID",
				vendor_id: "Vendor ID",
				member_count: "Members",
				last_message_at: "Last Message",
				no_messages: "No messages",
				messages_title: "Messages",
				placeholder_title: "Channel Preview Coming Soon",
				placeholder_description:
					"Full channel preview and messaging functionality will be available here.",
				close: "Close",
				confirmJoin: "Are you sure you want to join this conversation?",
				join: "Join…",
				joining: "Joining…",
			},
		},
		repConversations: {
			filterBar: {
				keyword: {
					label: "Keyword",
					placeholder: "Search representatives…",
				},
			},
			table: {
				RepConversationsTable: {
					emptyMessage: "No representatives found",
				},
				RepConversationsTableColumns: {
					user: "Representative",
					conversations: "Conversations",
					unread: "Unread",
					lastOnline: "Last online",
					lastMessage: "Last Message",
					actions: "Actions",
					view: "View",
				},
			},
			conversationList: {
				noMessages: "No messages",
				justNow: "Just now",
				minutesAgo_one: "{{count}} minute ago",
				minutesAgo_other: "{{count}} minutes ago",
				hoursAgo_one: "{{count}} hour ago",
				hoursAgo_other: "{{count}} hours ago",
				daysAgo_one: "{{count}} day ago",
				daysAgo_other: "{{count}} days ago",
				participants_one: "{{count}} participant",
				participants_other: "{{count}} participants",
				messages_one: "{{count}} message",
				messages_other: "{{count}} messages",
				lastMessage: "Last message",
			},
			conversationPreviewModal: {
				title: "Conversation Preview",
				conversation_info: "Conversation Information",
				preview_title: "Conversation preview (max last 30 messages)",
				vendor_label: "Vendor",
				participant_count: "Participants",
				last_message_at: "Last Message",
				no_messages: "No messages",
				close: "Close",
				confirmJoin:
					"Are you sure you want to join this conversation?",
				join: "Join…",
				joining: "Joining…",
			},
		},
		portalUsers: {
			form: {
				PortalAccountUserVendorLinkRow: {
					noTerritorySelected: "No territory (full access)",
				},
				PortalUserForm: {
					email: "Email",
					role: "Role",
					roleHelp:
						"Admin users have full control over the account. This means they can view all vendor data this account has access to, and can manage other users (including demoting your account if they choose).\n\nViewers can only view data for the vendors assigned to them, and can be associated with a territory to further limit their visibility.",
					adminWillHaveFullVendorAccess:
						"Once created, this admin user will have full access to all vendors.",
					adminHasFullVendorAccess:
						"This admin user can access all vendor data.",
					deactivateButton: "Deactivate…",
				},
			},
			table: {
				ActionCell: {
					reactivate: "Reactivate…",
					delete: "Delete…",
					edit: "Edit",
					copyInviteLink: "Copy invite link",
					resendInvite: "Resend invite",
					deactivate: "Deactivate…",
					menuAriaLabel: "User actions",
				},
				EmailCell: {
					deactivated: "Deactivated",
					pendingActivation: "Pending Activation",
					invited: "Invited {{timeAgo}}",
				},
				filterBar: {
					KeywordFilter: {
						label: "Keyword",
						placeholder: "Search by email or name…",
					},
				},
				PortalUsersTable: {
					emptyMessage: "No users found",
				},
				PortalUsersTableColumns: {
					name: "Name",
					role: "Role",
					vendors: "Vendors",
					createdAt: "Created at",
					email: "Email",
					actions: "Actions",
					noVendors: "No vendors",
				},
			},
		},
		VendorPriceDisplayConfigForm: {
			priceConfigTitle: "Price display configuration",
			priceConfigDescription:
				"Configure which price tiers are visible to different user types for {{vendor}}. Any price display configurations applied to specific users will take precedence.",
			certifiedUsersTitle: "Certified users",
			certifiedUsersDescription:
				"Configure price visibility for certified users who have verified resale certificate on file.",
			nonCertifiedUsersTitle: "Non-certified users",
			nonCertifiedUsersDescription:
				"Configure price visibility for users who do not have a verified resale certificate on file.",
			updateSuccess: "Vendor settings updated successfully.",
			updateError: "Failed to update vendor settings. Please try again.",
			priceLabels: {
				wholesalePrice: "Show wholesale price",
				stockingDealerPrice: "Show stocking dealer price",
				tradePrice: "Show trade price",
				imapPrice: "Show IMAP price",
				msrpPrice: "Show MSRP price",
			},
		},
		UserVendorSettingsForm: {
			settingsTitle: "Price display configuration",
			settingsDescription:
				"These following settings override any of {{vendorName}}'s price display configurations settings specifically for {{userName}}.",
			removeCustomConfiguration: "Remove custom price display configuration",
			visible: "Visible",
			hidden: "Hidden",
			updateSuccess: "User settings updated successfully.",
			updateError: "Failed to update user settings. Please try again.",
			resetSuccess: "User settings reset to default successfully.",
			resetError: "Failed to reset user settings. Please try again.",
			priceLabels: {
				wholesalePrice: "Show wholesale price",
				stockingDealerPrice: "Show stocking dealer price",
				tradePrice: "Show trade price",
				imapPrice: "Show IMAP price",
				msrpPrice: "Show MSRP price",
			},
		},
		ErrorFallback: {
			title: "Something went wrong",
			description:
				"We encountered an unexpected error. Our team has been notified and is looking into it. Please try refreshing the page or return to the dashboard.",
			tryAgain: "Try Again",
			goHome: "Go Home",
		},
		DashboardCardErrorFallback: {
			message: "Unable to load this card.",
			tryAgain: "Try again",
		},
		conversations: {
			ConversationSearchInput: {
				placeholder: "Search conversations…",
				unreadOnly: "Unread only",
			},
			MessageComposer: {
				placeholder: "Type a message...",
				send: "Send",
				sendError: "Failed to send message",
			},
			ConversationList: {
				noConversations: "No conversations yet",
			},
			ConversationHeader: {
				participants_one: "{{count}} participant",
				participants_other: "{{count}} participants",
			},
			InquiryCard: {
				productInquiry: "Product inquiry",
				vendorInquiry: "Vendor inquiry",
			},
			ParticipantItem: {
				youSuffix: " (You)",
				addingText: "Adding…",
				addText: "Add",
				repType: "Rep",
				designerType: "Designer",
			},
			ConversationErrorFallback: {
				conversationList: "Unable to load conversations.",
				messageArea: "Unable to load messages.",
				tryAgain: "Try again",
			},
			ParticipantsModal: {
				title: "Participants",
				loadingText: "Loading participants…",
				designersHeader_one: "Designer",
				designersHeader_other: "Designers ({{count}})",
				viewDesigner: "View account…",
				currentMembersHeader: "Current members ({{count}})",
				noMembersFound: "No members found",
				addRepsHeader: "Add reps",
				searchPlaceholder: "Search reps…",
				noRepsFoundSearch: "No reps found matching your search",
				noRepsAvailable: "No reps available to invite",
				closeButton: "Close",
			},
		},
	},

	/**
	 * Helper translations organized by filesystem hierarchy
	 *
	 * Examples:
	 *
	 * - t('helpers.errorHandling.handleUnexpectedError.title') - maps to app/Portal/resources/js/helpers/errorHandling.ts
	 */
	helpers: {
		errorHandling: {
			handleUnexpectedError: {
				title: "Unexpected error",
				suffix:
					"Please contact info@onsidedoor.com for help if this issue persists.",
			},
		},
	},

	/**
	 * Page translations organized by route hierarchy
	 *
	 * Examples:
	 *
	 * - t('pages.dashboard.index.title') - maps to app/pages/dashboard/index.tsx
	 * - t('pages.accounts.show.sidebar.businessName') - maps to UI element in app/pages/accounts/show.tsx
	 */
	pages: {
		dashboard: {
			index: {
				breadcrumb: "Dashboard",
				title: "DM Portal",
			},
		},
		"activity-feed": {
			index: {
				breadcrumb: "Activity feed",
				title: "Activity feed",
				productInteractionTypeFilterLabel: "Filter by product interaction type",
				allInteractionTypes: "All types",
				resumePolling: "Return to latest activity",
				updates_one: "{{count}} update",
				updates_other: "{{count}} updates",
				updateError: "Activity updates could not be loaded.",
			},
		},
		"product-performance": {
			index: {
				breadcrumb: "Product performance",
				title: "Product performance",
				description:
					"Products listed below have interactions. To view a product without interactions, use the jump to SKU feature above.",
			},
		},
		"territory-performance": {
			index: {
				breadcrumb: "Territory Performance",
				title: "Territory Performance",
			},
			show: {
				activityFeed: "Activity feed",
				sidebar: {
					territoryDetails: "Territory details",
					contactDesigners: "Contact designers",
					lifetime: "Lifetime",
					views: "Views",
					scans: "Scans",
					adds: "Adds",
					createdOn: "Created on",
					lastUpdatedOn: "Last updated on",
					viewers: "Viewers ({{count}} unique)",
					scanners: "Scanners ({{count}} unique)",
					collectors: "Collectors ({{count}} unique)",
					all: "All ({{count}} unique)",
					downloadCsv: "Download CSV",
				},
				notifications: {
					featureComingSoon: "Feature coming soon!",
				},
			},
		},
		products: {
			show: {
				breadcrumb: "Product details",
				title: "Product details",
				activityFeed: "Activity feed",
				imageAlt: "Photo of {{name}}",
				sidebar: {
					productDetails: "Product details",
					lifetime: "Lifetime",
					views: "Views",
					scans: "Scans",
					adds: "Adds",
					createdOn: "Created on",
					lastUpdatedOn: "Last updated on",
					barcodeMappings: "Barcode mappings",
					noBarcodeMappings: "No barcode mappings yet",
					addBarcode: "Add barcode",
					contactDesigners: "Contact designers",
					viewers: "Viewers ({{count}} unique)",
					scanners: "Scanners ({{count}} unique)",
					collectors: "Collectors ({{count}} unique)",
					all: "All ({{count}} unique)",
					downloadCsv: "Download CSV",
				},
			},
		},
		accounts: {
			index: {
				breadcrumb: "Accounts",
				title: "All accounts",
				errors: {
					loadAccountFailed: "Failed to load account data. Please try again.",
				},
			},
			show: {
				breadcrumb: "Account details",
				title: "Account details",
				activityFeed: "Activity feed",
				noLocation: "No location provided",
				messageButton: "Message",
				startConversationModal: {
					title: "Start Conversation",
					userLabel: "User",
					vendorLabel: "Vendor",
					messageLabel: "Message",
					userPlaceholder: "Select a user…",
					vendorPlaceholder: "Select a vendor…",
					messagePlaceholder: "Type your message…",
					sendButton: "Send Message",
					sendingButton: "Sending…",
					success: "Message sent successfully",
					validation: {
						userRequired: "Please select a user",
						vendorRequired: "Please select a vendor",
						messageRequired: "Please enter a message",
					},
					errors: {
						sendMessageFailed: "Failed to send message. Please try again.",
					},
					messages: {
						noUsers: "No users found for this account.",
						noVendors:
							"No vendors available for messaging users in this account.",
					},
				},
				sidebar: {
					account: "Account",
					businessName: "Business name",
					location: "Location",
					joinDate: "Join date",
					salesRepresentative: "Sales representative",
					name: "Name",
					email: "Email",
					phoneNumber: "Phone number",
					designerDetails: "Designer details",
					vendorSettingsTitle: "Vendor settings",
					vendorSettingsDescription:
						"Edit price display configuration for your users.",
					customPriceDisplay: "Price display",
				},
			},
			users: {
				vendors: {
					settings: {
						edit: {
							breadcrumb: "User settings",
							title: "User settings for {{vendorName}}",
							description:
								"Configure settings for {{userName}} when viewing {{vendorName}} products.",
						},
					},
				},
			},
		},
		tools: {
			breadcrumb: "Tools",
		},
		"qr-codes": {
			create: {
				breadcrumb: "QR code generator",
				title: "QR code generator",
			},
		},
		"barcode-mappings": {
			index: {
				breadcrumb: "Barcode mapping",
				title: "All barcode mappings",
				description:
					"Barcode mappings let designers using the Discover.Market app scan your existing product tags.",
				editButtonLabel: "Edit mappings",
			},
			edit: {
				breadcrumb: "Edit mapping",
				title: "Edit barcode mappings",
				description:
					"Barcode mappings let designers using the Discover.Market app scan your existing product tags. Many common barcode types are supported in the app: QR, UPC-A, UPC-E, EAN-13, EAN-8, Aztec Code, Codabar, Code 128, Code 93, Code 39, Data Matrix, ITF/ITF-14, and PDF417.",
			},
		},
		sessions: {
			create: {
				breadcrumb: "Sign in",
				title: "Sign in",
				submitButtonLabel: "Sign in",
				emailLabel: "Email",
				passwordLabel: "Password",
				forgotPasswordLink: "Forgot password?",
				remember: "Remember me",
			},
		},
		"forgot-password": {
			index: {
				breadcrumb: "Forgot password",
				title: "Request password reset",
				emailLabel: "Email",
				submitButtonLabel: {
					default: "Request reset",
					success: "Reset email sent!",
				},
				notification: {
					success:
						"Password reset sent! Please check your email for instructions.",
				},
			},
			show: {
				breadcrumb: "Change password",
				title: "Change password",
				newPasswordLabel: "New password",
				confirmPasswordLabel: "Confirm new password",
				submitButtonLabel: "Change password",
				notification: {
					success: "Password changed!",
				},
			},
		},
		error: {
			titles: {
				503: "503: Service unavailable",
				500: "500: Server error",
				404: "404: Page not found",
				403: "403: Forbidden",
				419: "419: Page expired",
				default: "Error",
			},
			descriptions: {
				503: "Sorry, we are doing some maintenance. Please check back shortly.",
				500: "We encountered an unexpected error. Our team has been notified and is looking into it.",
				404: "Sorry, the page you are looking for could not be found.",
				403: "Sorry, you are forbidden from accessing this page.",
				419: "The page expired, please try again.",
				default: "An error occurred.",
			},
			tryAgain: "Try Again",
			goHome: "Go Home",
		},
		home: {
			description:
				"The Discover.Market portal is invitation only. You can sign in to an existing account below, or contact our support team to learn more!",
			signInButtonLabel: "Sign in",
			learnMoreButtonLabel: "Learn more",
		},
		settings: {
			breadcrumb: "Settings",
			user: {
				show: {
					breadcrumb: "User settings",
					title: "User settings",
					twoFactorAuthentication: {
						title: "Two-factor authentication settings",
					},
				},
			},
			personalAccessTokens: {
				index: {
					title: "Personal Access Tokens",
					description:
						"Personal access tokens allow external applications to authenticate with the Portal API. Treat your tokens like passwords and keep them secure.",
					createButton: "Create Token",
					viewApiDocsButton: "View API Docs",
					emptyMessage: "You haven't created any personal access tokens yet.",
					table: {
						headers: {
							name: "Name",
							scopes: "Scopes",
							created: "Created",
							lastUsed: "Last Used",
							actions: "Actions",
						},
						never: "Never",
						revokeButton: "Revoke",
					},
					scopes: {
						read: "Read Access",
						export: "Export Access",
					},
				},
			},
		},
		setup: {
			index: {
				breadcrumb: "Setup",
				title: "Setup",
				description:
					"Thanks for confirming your email! Please provide a bit more information to finish setting up your account.",
				legalNameLabel: "Legal name",
				displayNameLabel: "First name/display name",
				passwordLabel: "Password",
				displayNameHint: "Used in emails and other general communications",
				passwordHint: "At least 8 characters",
				termsOfService: "terms of service",
				processingLabel: "Saving…",
				submitButtonLabel: "Next step",
			},
			noVendors: {
				description_Admin:
					"Your account doesn't have access to any vendors. Contact our support team if you believe this is an error.",
				description_Viewer:
					"Your account doesn't have access to any vendors. Ask your account admin for access, or contact our support team if you believe this is an error.",
				contactSupportButtonLabel: "Contact support",
				checkAgainButtonLabel: "Check again",
			},
			subscription: {
				title: "Subscription",
				description_Admin:
					"Please visit Stripe to configure your subscription and access the Discover.Market vendor portal.",
				description_Viewer:
					"Your account doesn't have an active Discover.Market vendor portal subscription. Please ask your account admin to configure the subscription, or contact our support team if you believe this is an error.",
				unreadMessagesNotice_one:
					"Your team has {{count}} unread message waiting! Activate your subscription to view and respond.",
				unreadMessagesNotice_other:
					"Your team has {{count}} unread messages waiting! Activate your subscription to view and respond.",
				contactSupportButtonLabel: "Contact support",
				configureButtonLabel: "Configure…",
			},
			subscriptionSuccess: {
				description:
					"Thanks for setting up your subscription. Your account is being configured. Please wait a moment and reload the page to check again.",
				contactSupportButtonLabel: "Contact support",
				checkAgainButtonLabel: "Check again",
			},
		},
		territories: {
			create: {
				breadcrumb: "Create",
				title: "Create new territory",
				description:
					"Territories allow admins to view data filtered by geographic area. Viewers can be assigned to a territory to limit their visibility to that area.",
			},
			edit: {
				breadcrumb: "Edit",
				title: "Edit {{name}}",
			},
			index: {
				breadcrumb: "Territories",
				title: "Manage territories",
				description:
					"Territories allow admins to view data filtered by geographic area. Viewers can be assigned to a territory to limit their visibility to that area.",
				createButtonLabel: "Create",
			},
		},
		"opted-out-users": {
			index: {
				breadcrumb: "Privacy opt-outs",
				title: "Privacy opt-outs",
				description:
					"Discover Market users (designers and retailers) who have opted out of selling or sharing their personal information within the last 90 days. If you previously received information from them, it should not be sold or shared in compliance with CCPA and other privacy laws.",
				tableHeaders: {
					user: "User",
					optOutDate: "Opt-out Date",
				},
				emptyMessage:
					"No users have opted out of selling or sharing personal information in the last 90 days",
			},
		},
		"portal-account-vendor-links": {
			index: {
				breadcrumb: "Vendors",
				title: "Vendors",
				description: "View all vendors that your account has access to.",
			},
			show: {
				priceDisplaySettings: "Price Display Settings",
				certifiedUsers: "Certified Users",
				nonCertifiedUsers: "Non-Certified Users",
				customConfigurationEnabled: "Custom configuration enabled",
				usingSystemDefaults: "Using system defaults",
				privacyOptOuts: {
					title: "Privacy opt-outs",
					description:
						"Discover Market users who have opted out of selling or sharing their personal information within the last 90 days.",
					viewButton: "View opt-outs",
				},
				products: {
					title: "Products",
					description: "View and manage products for this vendor.",
					viewButton: "View products",
				},
				editSettingsButton: "Edit Settings",
			},
			products: {
				index: {
					breadcrumb: "Products",
					title: "{{vendor}} Products",
					description:
						"View and manage products for this vendor. Use filters to find specific products by status or search by name and SKU.",
					addProductButton: "Add Product",
					importButton: "Import",
				},
				create: {
					breadcrumb: "Add Product",
					title: "Add Product for {{vendor}}",
					description: "Create a new product for this vendor.",
				},
				edit: {
					breadcrumb: "Edit Product",
					title: "Edit {{product}}",
				},
				import: {
					create: {
						breadcrumb: "Import Products",
						title: "Import Products for {{vendor}}",
						description:
							"Upload a CSV file to bulk update products. Match products by SKU.",
						step1Title: "Step 1: Get Your Template",
						step1Description:
							"Download a CSV template pre-filled with your existing products.",
						step2Title: "Step 2: Upload Your File",
						step2Description:
							"Upload your completed CSV file to update your products.",
						templateTitle: "CSV Template",
						templateDescription:
							"The template includes all your existing products with their current values. Edit the file and re-upload to make changes.",
						fileLabel: "CSV File",
						allowCreateLabel: "Allow creating new products",
						allowCreateDescription:
							"When enabled, rows with SKUs that don't exist will create new products instead of being skipped.",
						uploading: "Uploading... {{progress}}%",
						submit: "Upload and Import",
						downloadTemplate: "Download template",
						helpTitle: "Import tips",
						helpItem1:
							"Download the template to see the expected format with your existing products.",
						helpItem2:
							"Products are matched by SKU. The SKU column is required.",
						helpItem3:
							"Columns with values will update the field. Empty columns will clear the field.",
						helpItem4:
							"Omit a column entirely to preserve existing values.",
					},
					show: {
						breadcrumb: "Import Status",
						title: "Import Status for {{vendor}}",
						processing: "Processing your import...",
						processingDescription: "This may take a few moments",
						rowCount: "{{count}} rows",
						completed: "Import completed successfully!",
						completedAllSuccess: "All {{count}} products have been updated",
						completedWithErrors: "Import completed with errors",
						completedWithErrorsDescription:
							"{{success}} products updated, {{errors}} rows had issues",
						successful: "Updated",
						errors: "Errors",
						viewProducts: "View Products",
						importMore: "Import More",
						downloadErrors: "Download error details",
					},
				},
			},
			settings: {
				edit: {
					breadcrumb: "Vendor settings",
					title: "Vendor settings",
				},
			},
			"privacy-opt-outs": {
				index: {
					breadcrumb: "Privacy opt-outs",
					title: "Privacy opt-outs",
					description:
						"Discover Market users (designers and retailers) who have opted out of selling or sharing their personal information within the last 90 days for {{vendorName}}. If you previously received information from them, it should not be sold or shared in compliance with CCPA and other privacy laws.",
					tableHeaders: {
						user: "User",
						optOutDate: "Opt-out Date",
					},
					emptyMessage:
						"No users have opted out of selling or sharing personal information in the last 90 days for this vendor",
				},
			},
		},
		"two-factor-challenge": {
			create: {
				breadcrumb: "Two-factor authentication",
				title: "Two-factor authentication code",
				recoveryCodeLabel: "Recovery code",
				oneTimePasswordLabel: "One-time password",
				submitButtonLabel: "Verify one-time password",
				helpText: {
					recoveryCode:
						"Please confirm access to your account by entering one of your emergency recovery codes.",
					oneTimePassword:
						"Please confirm access to your account by entering the one-time password provided by your authenticator application.",
				},
				toggleButtons: {
					useOneTimePassword: "Use one-time password",
					useRecoveryCode: "Use a recovery code",
				},
			},
		},
		"rep-messaging": {
			index: {
				breadcrumb: "Rep conversations",
				title: "Rep conversations",
				description: "View all representatives and their messaging activity.",
			},
			show: {
				title: "{{user}} - Rep conversations",
				description: "View messaging channels for this representative.",
				channels_title: "Message Channels",
				no_channels: "No message channels found for this representative.",
			},
		},
		"rep-conversations": {
			index: {
				breadcrumb: "Rep conversations",
				title: "Rep conversations",
				description:
					"View all representatives and their conversation activity.",
			},
			show: {
				title: "{{user}} - Rep conversations",
				description:
					"View conversations for this representative.",
				conversations_title: "Conversations",
				no_conversations:
					"No conversations found for this representative.",
			},
		},
		messaging: {
			breadcrumb: "Messaging",
		},
		conversations: {
			index: {
				breadcrumb: "Conversations",
				title: "Conversations",
				emptyState: "Select a conversation to start messaging.",
				noConversations: "No conversations yet. Messages from designers will appear here.",
				loading: "Loading messages...",
			},
			show: {
				breadcrumb: "Conversation",
				title: "Conversation",
			},
		},
		takeout: {
			index: {
				breadcrumb: "Data Export",
				title: "Data Export",
				description:
					"Download all your interaction data in a portable format. This includes account information, contact details, and interaction history.",
				downloadButton: "Download ZIP",
				newExportButton: "New Export",
				requestButton: "Request Export",
				exportInProgress: "Exporting...",
				retryButton: "Retry Export",
				exportProcessingTitle: "Preparing your export...",
				exportInProgressMessage:
					"Your data is being packaged. This may take a few minutes for large datasets.",
				exportReadyTitle: "Your export is ready!",
				exportReadyMessage:
					"Your data has been packaged and is ready to download.",
				exportContains:
					"ZIP contains accounts.csv, contacts.csv, and interactions.csv",
				startedAt: "Started {{time}}",
				completedAt: "Completed {{time}}",
				failedAt: "Failed {{time}}",
				exportFailedTitle: "Export failed",
				exportFailedMessage:
					"Export failed. Please try again or contact support if the issue persists.",
				notifications: {
					exportStarted:
						"Export started. This page will update when it's ready.",
					exportFailed: "Failed to start export. Please try again.",
				},
				exportCard: {
					title: "Export Your Data",
					description: "Your data export will include the following CSV files:",
					vendorLabel: "Select a vendor",
					vendorPlaceholder: "Choose a vendor...",
					items: {
						accounts:
							"Accounts - Account list with interaction summary (views, scans, collection adds)",
						contacts: "Contacts - User contact information for each account",
						interactions:
							"Interactions - Complete interaction history with product details",
					},
				},
			},
		},
		news: {
			title: "News channel",
			channelTitle: "Discover.Market News",
			channelSubtitle:
				'Warning: Sending a message here will deliver a push notification to all users who have enabled them! Use the "Internal link" button to display a link card in the DM interface.',
			loading: "Loading the news channel…",
			error:
				"We couldn't load the news channel. Please try again in a few moments.",
			streamUnavailable:
				"Messaging is not currently available. Refresh the page or try again later.",
			internalLinkModal: {
				title: "Insert Internal Link",
				description:
					"Create a tappable link card that appears in the Discover.Market mobile app",
				useCustomTitle: "Use custom display title",
				displayTitle: "Display Title",
				displayTitlePlaceholder: "e.g. Spring Sale, New Arrivals",
				displayTitleHelp:
					"This will be displayed on the link card instead of the formatted path",
				path: "Path",
				pathPlaceholder: "/catalog/products?colors=green",
				pathHelp: "Include query parameters if needed (e.g. ?colors=green)",
				syntaxPreview: "Syntax preview",
				syntax: "Syntax:",
				cancel: "Cancel",
				insertLink: "Insert Link",
			},
			internalLinkButton: {
				label: "Internal link",
				ariaLabel: "Insert internal link",
			},
		},
		newsNative: {
			breadcrumb: "News native",
			title: "News native",
			description: "Preview and manage news posts for Discover.Market.",
			createTitle: "Create news post",
			createHint: "Posts are saved as drafts. You can publish when ready.",
			bodyLabel: "Post body",
			createPlaceholder:
				"Write a post. Internal links using double-brace syntax are supported.",
			createButton: "Save draft",
			draftsTitle: "Draft posts",
			publishedTitle: "Published posts",
			emptyDrafts: "No draft posts yet.",
			emptyPublished: "No published posts yet.",
			statusDraft: "Draft",
			statusPublished: "Published",
			createdLabel: "Created {{date}}",
			publishedLabel: "Published {{date}}",
			readByLabel: "Read by {{count}}",
			editButton: "Edit",
			saveButton: "Save",
			cancelButton: "Cancel",
			deleteButton: "Delete",
			publishButton: "Publish",
			unpublishButton: "Unpublish",
			confirmDelete: "Delete this post?",
			deleteConfirmTitle: "Delete post",
			deleteConfirmMessage:
				"Are you sure you want to delete this post? This action cannot be undone.",
		},
		"portal-users": {
			create: {
				breadcrumb: "Invite user",
				title: "Invite a new user",
				description:
					"This user will be emailed a link to complete their profile information and join your account.",
			},
			index: {
				title: "Users",
				description: "Manage or create new users.",
				breadcrumb: "Users",
				inviteButtonLabel: "Invite user",
			},
			edit: {
				breadcrumb: "Edit user",
				title: "Edit {{email}}",
				description: "Change role or update associated vendors for viewers.",
			},
		},
	},

	/**
	 * Shared translations used across multiple components/pages
	 *
	 * Examples:
	 *
	 * - t('shared.contactSupport') - maps to reusable text across components
	 * - t('shared.productInteraction.description.scan', {0: productName, 1: userName}) - maps to formatted message with parameters
	 */
	shared: {
		all: "All",
		contactSupport: "Contact support",
		create: "Create",
		edit: "Edit",
		sendEmail: "Send email",
		view: "View",
		export: "Export",
		of: "of",
		save: "Save",
		saving: "Saving…",
		invite: "Invite",
		show: "Show",
		hide: "Hide",
		cancel: "Cancel",
		error: "Error",
		done: "Done",
		clear: "Clear",
		productInteraction: {
			description: {
				scan: "<1>{{productName}}</1> by {{vendorName}} was scanned by {{userName}} at <2>{{accountName}}</2>",
				view: "<1>{{productName}}</1> by {{vendorName}} was viewed by {{userName}} at <2>{{accountName}}</2>",
				addToCollectionInspiration:
					"<1>{{productName}}</1> by {{vendorName}} was added to an inspiration collection by {{userName}} at <2>{{accountName}}</2>",
				addToCollectionClient:
					"<1>{{productName}}</1> by {{vendorName}} was added to a client collection by {{userName}} at <2>{{accountName}}</2>",
				addToCollection:
					"<1>{{productName}}</1> by {{vendorName}} was added to a collection by {{userName}} at <2>{{accountName}}</2>",
			},
		},
		PortalUserRole: {
			admin: "Admin",
			viewer: "Viewer",
		},
		notifications: {
			personalAccessToken: {
				created: "Personal access token created successfully",
				createError: "Failed to create personal access token",
				revoked: "Personal access token revoked successfully",
				revokeError: "Failed to revoke personal access token",
			},
		},
	},

	stores: {
		TerritoriesTableFilters: {
			filters: {
				keyword: "Keyword",
				sortBy: "Sort by",
				sortDirection: "Sort direction",
				page: "Page",
				perPage: "Per page",
			},
		},
		VendorsTableFilters: {
			filters: {
				keyword: "Keyword",
				sortBy: "Sort by",
				sortDirection: "Sort direction",
				page: "Page",
				perPage: "Per page",
			},
		},
		PortalUsersTableFilters: {
			filters: {
				keyword: "Keyword",
				sortBy: "Sort by",
				sortDirection: "Sort direction",
				page: "Page",
				perPage: "Per page",
			},
		},
		VendorProductsTableFilters: {
			filters: {
				keyword: "Keyword",
				status: "Status",
				category: "Category",
				sortBy: "Sort by",
				sortDirection: "Sort direction",
				page: "Page",
				perPage: "Per page",
			},
		},
	},

	/**
	 * Utility function translations
	 */
	util: {
		streamClient: {
			confirmLeaveChannel: "Are you sure you want to leave this conversation?",
			streamNotInitialized: "Stream chat not initialized",
			failedToLeaveChannel: "Failed to leave channel",
			cannotLeaveDesignerAlone:
				"You can't leave a designer alone in a conversation. To leave, please invite another representative first.",
		},
	},
} as const

export default translation
